// Utilities to make HTTP requests to an API. Includes code to send the Rails CSRF token
import { getMetaTag } from "@/shared/util/dom"

/**
 * Make a fetch request, but include the X-CSRF-Token for non-GET requests
 * and X-Requested-With=XMLHttpRequest headers for Rails to work.
 * All other options will be respected. In particular, users should
 * still specify "Content-Type" and "Accept" headers, as this is a
 * low-level function.
 *
 * Note on error handling: This is very difficult to get right.
 * The fetch API will only reject the promise if the request fails,
 * not if it returns a non-200 status code. This means that the
 * promise will resolve even if the server returns a 500 error.
 * Therefore, before accessing the response body, you should check
 * the status code and take some action if it is not what you expect
 * (e.g. 200). This is not done here because it is difficult to know
 * what the right error handling strategy is for a given use-case.
 *
 * @param {object|string} input first argument to window.fetch
 * @param {object} init second argument to window.fetch
 * @returns {Promise} the promise returned by window.fetch
 */
export function railsFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const upperMethod = init?.method?.toUpperCase()
  const mergedHeaders = mergeRailsHeaders(upperMethod, init?.headers)

  const fetchInit: RequestInit = {
    ...init,
    method: upperMethod,
    headers: mergedHeaders,
  }

  // console.debug("fetch init obj:", fetchInit)

  return window.fetch(input, fetchInit)
}

/**
 * @deprecated Use `railsFetch` instead for clarity.
 */
export const fetch = railsFetch;

/**
 * Safely merge headers from init with headers expected by Rails.
 * Handles both Headers objects and plain objects
 *
 * @param {string} method the HTTP method (used to determine if the CSRF token should be added) in uppercase
 * @param {object} initHeaders headers to merge with Rails headers
 * @returns {Headers} the merged headers
 */
function mergeRailsHeaders(method?: string, initHeaders?: HeadersInit): Headers {
  const headers = new Headers(initHeaders)

  // Tells Rails that this is an AJAX request, so `request.xhr?` will return true.
  headers.set("X-Requested-With", "XMLHttpRequest")

  // If the method is POST, PUT, PATCH, or DELETE, add the CSRF token.
  if (method === "POST" || method === "PUT" || method === "PATCH" || method === "DELETE") {
    headers.set("X-CSRF-Token", getMetaTag("csrf-token") || "")
  }

  return headers
}

/**
 * Merge newHeaders into baseHeaders. Returns a new Headers object. Does nothing if
 * newHeaders is not provided.
 * @param baseHeaders - The headers to merge into
 * @param newHeaders - The headers to merge into baseHeaders
 * @returns A new Headers object with the merged headers
 */
export function mergeHeaders(baseHeaders: HeadersInit, newHeaders?: HeadersInit): Headers {
  const mergedHeaders = new Headers(baseHeaders);
  if (!newHeaders) {
    return mergedHeaders;
  }

  if (newHeaders instanceof Headers) {
    for (const [key, value] of newHeaders.entries()) {
      mergedHeaders.set(key, value);
    }
  } else if (Array.isArray(newHeaders)) {
    for (const [key, value] of newHeaders) {
      mergedHeaders.set(key, value);
    }
  } else {
    for (const [key, value] of Object.entries(newHeaders)) {
      mergedHeaders.set(key, value);
    }
  }

  return mergedHeaders
}

/**
 * Additional options passed to window.fetch.
 */
type FetchOpts = {
  method?: string
  body?: string
  headers?: { [key: string]: string}
  signal?: AbortSignal
}

/**
 * Additional options for #request.
 */
type RequestOpts = {
  rawBody?: boolean
  body?: string
  rawResponse?: boolean
  noContentType?: boolean
  returnResponse?: boolean
}


/**
 * Wrapper around fetch API that includes
 *
 * @param {string} url the url to fetch
 * @param {object|string|null} data JSON or raw data
 * @param {object} opts options to pass directly to fetch
 * @param {object} requestOpts custom options for this function
 * @returns {Promise} the fetch result
 */
export async function request(url: string, data: object | string | null = null, opts: FetchOpts = {}, requestOpts: RequestOpts = {}): Promise<any> {
  const defaultOpts : FetchOpts = {
    method: "POST",
    headers: {
		  "Accept": "application/javascript",
		  "Content-Type": "application/json",
    },
  }

  if (requestOpts.rawBody && data) {
    defaultOpts.body = data as string
  } else if (data) {
  	defaultOpts.body = JSON.stringify(data)
  }

  const mergedOpts = {...defaultOpts, ...opts}

  if (requestOpts["noContentType"] && mergedOpts.headers) {
    delete mergedOpts.headers["Content-Type"];
    delete mergedOpts.headers["Accept"];
  }

  const response = await fetch(url, mergedOpts)
  if (requestOpts.rawResponse) {
    const body = await response.text()
    if (requestOpts.returnResponse) {
      return [body, response]
    }
    return body
  } else {
    const json = await response.json() as string
    // note: this is to get around the any linter warning, json is actually json
    if (requestOpts.returnResponse) {
      return [json, response]
    }
    return json
  }
}

export const post = (url: string, data: object | string | null, signal?: AbortSignal | undefined) => {
  return request(url, data, {method: "POST", signal})
}

export const postFormData = (url: string, formData: string) => {
  const opts = {
    method: "POST",
  }
  return request(url, formData, opts, {rawBody: true, noContentType: true})
}

export const get = (url: string) => {
  return request(url, null, {method: "GET"})
}

export const getRaw = (url: string) => {
  return request(url, null, {method: "GET"}, {rawResponse: true})
}

export const put = (url: string, data: object | string | null) => {
  return request(url, data, {method: "PUT"})
}

export const patch = (url: string, data: object | string | null) => {
  return request(url, data, {method: "PATCH"})
}

export const del = (url: string, data: object | string | null) => {
  return request(url, data, {method: "DELETE"})
}
